import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  CircularProgress,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack,
  Download,
  AttachFile,
  Warning,
  CheckCircle,
  Business,
  Person,
  Email,
  Phone,
  LocationOn,
  Today,
  Inventory,
  Category,
  Assignment,
  Edit,
  Refresh,
  ExpandMore,
  ExpandLess,
  Comment,
} from '@mui/icons-material';
import { useAuth } from 'react-oidc-context';
import appConfig from '../config/app';

const createApiService = (auth) => ({
  async getRMADetails(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;
    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/details/?detailed=true`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || 'Unknown error'}`);
    }
    return response.json();
  },

  async downloadAttachment(attachmentId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;
    const response = await fetch(`${appConfig.api.baseUrl}/rma/attachments/${attachmentId}/download/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response;
  },

  async exportRMA(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;
    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/export/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.blob();
  },

  async respondToFieldIssue(issueId, response) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;
    const apiResponse = await fetch(`${appConfig.api.baseUrl}/rma/field-issues/${issueId}/respond/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        customer_response: response,
      }),
    });
    if (!apiResponse.ok) {
      throw new Error(`HTTP error! status: ${apiResponse.status}`);
    }
    return apiResponse.json();
  },
});

const fieldLabels = {
  company_name: 'Company Name',
  contact_person: 'Contact Person',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  city: 'City',
  postal_code: 'Postal Code',
  country: 'Country',
  product_name: 'Product Name',
  product_code: 'Product Code',
  serial_number: 'Serial Number',
  purchase_date: 'Purchase Date',
  delivery_note_number: 'Delivery Note Number',
  description: 'Description',
};

const fieldIcons = {
  company_name: <Business sx={{ color: 'text.secondary', fontSize: 18 }} />,
  contact_person: <Person sx={{ color: 'text.secondary', fontSize: 18 }} />,
  email: <Email sx={{ color: 'text.secondary', fontSize: 18 }} />,
  phone: <Phone sx={{ color: 'text.secondary', fontSize: 18 }} />,
  address: <LocationOn sx={{ color: 'text.secondary', fontSize: 18 }} />,
  city: <LocationOn sx={{ color: 'text.secondary', fontSize: 18 }} />,
  postal_code: <LocationOn sx={{ color: 'text.secondary', fontSize: 18 }} />,
  country: <LocationOn sx={{ color: 'text.secondary', fontSize: 18 }} />,
  product_name: <Inventory sx={{ color: 'text.secondary', fontSize: 18 }} />,
  product_code: <Category sx={{ color: 'text.secondary', fontSize: 18 }} />,
  serial_number: <CheckCircle sx={{ color: 'text.secondary', fontSize: 18 }} />,
  purchase_date: <Today sx={{ color: 'text.secondary', fontSize: 18 }} />,
  delivery_note_number: <Assignment sx={{ color: 'text.secondary', fontSize: 18 }} />,
};

const ExpandableDescription = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const maxLength = 100;

  if (!text) {
    return (
      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          No description provided
        </Typography>
      </Box>
    );
  }

  const isLong = text.length > maxLength;
  const displayText = expanded || !isLong ? text : `${text.substring(0, maxLength)}...`;

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200', width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflowWrap: 'anywhere', display: 'block', width: '100%' }}>
        {displayText}
      </Typography>
      {isLong && (
        <Button
          size="small"
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          sx={{ mt: 1, p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
        >
          {expanded ? 'Show less' : 'Read more'}
        </Button>
      )}
    </Box>
  );
};

const FieldRow = ({ field, value }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      px: 1.5,
      py: 1,
      borderRadius: 1.5,
      transition: 'background-color 0.15s ease',
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    <Box sx={{ mt: 0.25, flexShrink: 0, color: 'text.disabled' }}>
      {fieldIcons[field]}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.disabled"
        fontWeight={700}
        sx={{ letterSpacing: '0.06em', display: 'block', lineHeight: 1.4 }}
      >
        {field.replace(/_/g, ' ').toUpperCase()}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere', mt: 0.15 }}>
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

const FieldIssueCard = ({ issue, onRespond, userType }) => {
  const [responseText, setResponseText] = useState('');
  const [showResponse, setShowResponse] = useState(false);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  const handleSubmitResponse = () => {
    if (responseText.trim()) {
      onRespond(issue.id, responseText.trim());
      setResponseText('');
      setShowResponse(false);
    }
  };

  return (
    <Accordion
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: '10px !important',
        border: '1px solid',
        borderColor: 'divider',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { borderColor: 'primary.light' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {fieldIcons[issue.field_name]}
            <Typography variant="subtitle2" fontWeight={700}>
              {fieldLabels[issue.field_name]}
            </Typography>
          </Box>
          <Chip size="small" label={issue.severity.toUpperCase()} color={getSeverityColor(issue.severity)} />
          {issue.resolved && <Chip size="small" label="Resolved" color="success" icon={<CheckCircle />} />}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'block', px: 2, pb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: '0.05em' }}>
            ISSUE DESCRIPTION
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {issue.description || 'No description available'}
          </Typography>
        </Box>

        {issue.customer_response && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.100' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: '0.05em' }}>
              YOUR RESPONSE
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
              {issue.customer_response}
            </Typography>
          </Box>
        )}

        {userType === 'customer' && !issue.resolved && !issue.customer_response && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Comment />}
            onClick={() => setShowResponse(!showResponse)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Respond
          </Button>
        )}

        {showResponse && userType === 'customer' && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Enter your response..."
              size="small"
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmitResponse}
                disabled={!responseText.trim()}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Submit
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowResponse(false)}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const RMADetailsView = ({ rmaId, onBack, onEdit, onDownload, refreshTrigger, userType }) => {
  const auth = useAuth();
  const [rmaData, setRmaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const apiService = createApiService(auth);

  const loadRMADetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getRMADetails(rmaId);
      setRmaData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (rmaId) loadRMADetails();
  }, [rmaId]);

  useEffect(() => {
    if (refreshTrigger > 0 && rmaId) loadRMADetails();
  }, [refreshTrigger, rmaId]);

  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      const response = await apiService.downloadAttachment(attachmentId);
      const data = await response.json();
      const { download_url } = data;
      const a = document.createElement('a');
      a.href = download_url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setSnackbarMessage('Attachment downloaded successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Error downloading attachment: ' + err.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleExportRMA = async () => {
    try {
      const blob = await apiService.exportRMA(rmaId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${rmaData.rma_number}-export.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbarMessage('RMA exported successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Error exporting RMA: ' + err.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleRespondToIssue = async (issueId, response) => {
    try {
      await apiService.respondToFieldIssue(issueId, response);
      setRmaData(prev => ({
        ...prev,
        field_issues: prev.field_issues.map(issue =>
          issue.id === issueId ? { ...issue, customer_response: response } : issue
        ),
      }));
      setSnackbarMessage('Response submitted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Error submitting response: ' + err.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'primary';
      case 'under_review': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'needs_info': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();
  const formatDateTime = (dateString) => new Date(dateString).toLocaleString();

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={loadRMADetails} sx={{ borderRadius: 2, textTransform: 'none' }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!rmaData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>No RMA data available</Alert>
      </Box>
    );
  }

  const unresolvedIssues = rmaData.field_issues?.filter(issue => !issue.resolved) || [];
  const resolvedIssues = rmaData.field_issues?.filter(issue => issue.resolved) || [];

const SectionCard = ({ title, children }) => (
  <Card
    elevation={0}
    sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      overflow: 'hidden',
      minWidth: 0,
      '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
    }}
  >

    <Box sx={{ bgcolor: '#126F42', px: '20px', py: '12px' }}>
      <Typography variant="subtitle1" fontWeight={700} color="#FFFFFF">
        {title}
      </Typography>
    </Box>
    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: '20px !important', minWidth: 0, overflow: 'hidden' }}>
      <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%', overflow: 'hidden' }}>
        {children}
      </Box>
    </CardContent>
  </Card>
);
  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Back
          </Button>
          <Typography variant="h5" fontWeight={700}>
            {rmaData.rma_number}
          </Typography>
          <Chip
            label={rmaData.status?.replace(/_/g, ' ').toUpperCase()}
            color={getStatusColor(rmaData.status)}
            sx={{ fontWeight: 700, borderRadius: 1.5 }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadRMADetails}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportRMA}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Export
          </Button>
          {userType === 'serviceman' && onEdit && (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => onEdit(rmaId, 'edit')}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Edit RMA
            </Button>
          )}
        </Box>
      </Box>

      {unresolvedIssues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 4, borderRadius: 2 }}>
          <Typography variant="body2" fontWeight="600">
            This RMA has {unresolvedIssues.length} unresolved field issue(s) that need attention.
            {userType === 'customer' && ' You can respond to these issues below.'}
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(12, minmax(0, 1fr))' }, gap: 3, mb: 3, width: '100%' }}>

        <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 4' }, display: 'flex', minWidth: 0, overflow: 'hidden' }}>
          <SectionCard title="Company Information">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mx: -1.5 }}>
              {['company_name', 'contact_person', 'email', 'phone'].map(field => (
                <FieldRow key={field} field={field} value={rmaData[field]} />
              ))}
            </Box>
          </SectionCard>
        </Box>

        <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 4' }, display: 'flex', minWidth: 0, overflow: 'hidden' }}>
          <SectionCard title="Address Information">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mx: -1.5 }}>
              {['address', 'city', 'postal_code', 'country'].map(field => (
                <FieldRow key={field} field={field} value={rmaData[field]} />
              ))}
            </Box>
          </SectionCard>
        </Box>

        <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 4' }, display: 'flex', minWidth: 0, overflow: 'hidden' }}>
          <SectionCard title="Product Information">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mx: -1.5 }}>
              {['product_name', 'product_code', 'serial_number', 'purchase_date', 'delivery_note_number'].map(field => (
                <FieldRow
                  key={field}
                  field={field}
                  value={field === 'purchase_date' && rmaData[field] ? formatDate(rmaData[field]) : rmaData[field]}
                />
              ))}
            </Box>
          </SectionCard>
        </Box>

        <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 4' }, display: 'flex', minWidth: 0, overflow: 'hidden' }}>
          <SectionCard title={`Attachments (${rmaData.attachments?.length || 0})`}>
            {rmaData.attachments?.length > 0 ? (
              <List dense disablePadding sx={{ mx: -1 }}>
                {rmaData.attachments.map((attachment, index) => (
                  <React.Fragment key={attachment.id}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem disableGutters sx={{ py: 1, px: 1, borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <AttachFile color="action" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight="600" sx={{ wordBreak: 'break-all' }}>
                            {attachment.original_filename}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(attachment.file_size)} · {formatDateTime(attachment.uploaded_at)}
                          </Typography>
                        }
                      />
                      <IconButton color="primary" size="small" onClick={() => handleDownloadAttachment(attachment.id, attachment.original_filename)}>
                        <Download fontSize="small" />
                      </IconButton>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No attachments available
              </Typography>
            )}
          </SectionCard>
        </Box>

        <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 8' }, display: 'flex', minWidth: 0, overflow: 'hidden' }}>
          <SectionCard title="Problem Description">
            <ExpandableDescription text={rmaData.description} />
          </SectionCard>
        </Box>

        {rmaData.field_issues?.length > 0 && (
          <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 12' }, minWidth: 0, overflow: 'hidden' }}>
            <SectionCard title={`Field Issues (${rmaData.field_issues.length})`}>
              {unresolvedIssues.length > 0 && (
                <Box sx={{ mb: resolvedIssues.length > 0 ? 3 : 0 }}>
                  <Typography variant="body2" color="error" fontWeight={700} sx={{ mb: 1.5 }}>
                    Unresolved ({unresolvedIssues.length})
                  </Typography>
                  {unresolvedIssues.map(issue => (
                    <FieldIssueCard key={issue.id} issue={issue} onRespond={handleRespondToIssue} userType={userType} />
                  ))}
                </Box>
              )}
              {resolvedIssues.length > 0 && (
                <Box>
                  <Typography variant="body2" color="success.main" fontWeight={700} sx={{ mb: 1.5 }}>
                    Resolved ({resolvedIssues.length})
                  </Typography>
                  {resolvedIssues.map(issue => (
                    <FieldIssueCard key={issue.id} issue={issue} onRespond={handleRespondToIssue} userType={userType} />
                  ))}
                </Box>
              )}
            </SectionCard>
          </Box>
        )}

        <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 12' }, minWidth: 0, overflow: 'hidden' }}>
          <SectionCard title="Timeline">
            <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {[
                { label: 'SUBMITTED', value: rmaData.submitted_at },
                { label: 'CREATED', value: rmaData.created_at },
                { label: 'LAST UPDATED', value: rmaData.updated_at },
              ]
                .filter(item => item.value)
                .map(item => (
                  <Box key={item.label}>
                    <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ letterSpacing: '0.06em', display: 'block' }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.25 }}>
                      {formatDateTime(item.value)}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </SectionCard>
        </Box>

      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RMADetailsView;